/**
 * Labstep
 *
 * @module components/ProtocolValue/SmartTable/Modal
 * @desc Protocol Condition Modal
 */

import { EntityViewFormShowEditName } from 'labstep-web/components/EntityView/Form/ShowEdit/Name';
import { useGridSmartTableProtocolValueServiceFromContext } from 'labstep-web/contexts/grid/hook';
import type { ParamsStateContextType } from 'labstep-web/contexts/params-state/types';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { ModalDefaultHeader as ModalHeader } from 'labstep-web/core/Modal/Default/Header';
import { DataGridExportAsCSV } from 'labstep-web/grid/Index/components/DataGridExportAsCSV';
import { ExportProgressProvider } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridIndexActionShowSideBar as EntityDataGridActionShowSideBar } from 'labstep-web/grid/Index/components/ShowSideBar';
import { ToolPanelProvider } from 'labstep-web/grid/Index/components/ToolPanel/context';
import {
  getSystemFilterExperimentGuid,
  getSystemFilterProtocolGuid,
} from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { GridFilterService } from 'labstep-web/grid/services/grid-filter.service';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import type React from 'react';
import { ProtocolValueSmartTableModalContent } from './Content';
import styles from './styles.module.scss';
import type { ProtocolValueSmartTableModalProps } from './types';

export const ProtocolValueSmartTableModal: React.FC<
  ProtocolValueSmartTableModalProps
> = ({
  experimentOrProtocol,
  entityView,
  open = false,
  onClose,
  viewComponent,
}) => {
  const gridSmartTableProtocolValueService =
    useGridSmartTableProtocolValueServiceFromContext();

  const { activeGroup } = useActiveGroup();

  const paramsStateContext: ParamsStateContextType = {
    isGrid: true,
    name: 'smart_table',
    entityView,
    initialSearchParams:
      GridFilterService.injectSystemFilter(
        experimentOrProtocol instanceof Experiment
          ? getSystemFilterExperimentGuid(experimentOrProtocol.guid)
          : getSystemFilterProtocolGuid(experimentOrProtocol.guid),
        entityView.parameters,
      ) || {},
    loadedEntityView: new EntityView({
      ...entityView,
    }),
  };
  if (paramsStateContext.loadedEntityView) {
    paramsStateContext.loadedEntityView.parameters =
      paramsStateContext.initialSearchParams;
  }

  return (
    <ExportProgressProvider>
      <ToolPanelProvider>
        <Modal
          className={styles.modal}
          dataCy="components/ProtocolValue/Modal"
          header={({ toggleModal }) => (
            <ModalHeader
              text={
                <EntityViewFormShowEditName entityView={entityView} />
              }
              onClose={() => {
                if (onClose) {
                  onClose();
                }
                if (
                  entityView.id &&
                  gridSmartTableProtocolValueService
                ) {
                  gridSmartTableProtocolValueService.updateEntityViewColIds(
                    entityView,
                  );
                }

                toggleModal();
              }}
              extraActions={
                experimentOrProtocol.isLocked
                  ? [
                      <DataGridExportAsCSV
                        className={styles.searchBarAction}
                      />,
                    ]
                  : [
                      <EntityDataGridActionShowSideBar
                        className={styles.searchBarAction}
                      />,
                      <DataGridExportAsCSV
                        className={styles.searchBarAction}
                      />,
                    ]
              }
            />
          )}
          size="fullscreen"
          closeIcon={false}
          initialModalState={open}
          onClose={onClose}
          content={
            <ProtocolValueSmartTableModalContent
              experimentOrProtocol={experimentOrProtocol}
              entityView={entityView}
              group={activeGroup!}
              entities={experimentOrProtocol.protocol_values}
            />
          }
          viewComponent={viewComponent}
        />
      </ToolPanelProvider>
    </ExportProgressProvider>
  );
};
